import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '@/store';
import { env } from '@/config/env';

interface PermissionRouteProps {
  children: React.ReactElement;
  check: (userRoles: string[], organization: string) => boolean;
}

/**
 * Generic protected route wrapper — redirects to /dashboard unless `check`
 * passes for the current user's roles/organization. Route guards like
 * GlobalAdminRoute are thin wrappers over this so new permission-scoped
 * modules don't need their own one-off *Route component.
 */
const PermissionRoute: React.FC<PermissionRouteProps> = ({ children, check }) => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);

  // Bypass guard when authentication is disabled (development mode)
  if (!env.authEnabled && !env.authorizationEnabled) {
    return children;
  }

  // If not authenticated, redirect to login (handled by AuthGuard)
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user || !check(user.roles, user.organizationName ?? '')) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default PermissionRoute;
